import { useCallback, useMemo, useState } from 'react';
import type { AreaTreeNode, WorkPositionTreeNode } from '../../dialog/employee/hooks/AssignPosition/useAssignPositionForm';

export type FlatItemType = 'subcompany' | 'area' | 'work_position';

export interface FlatTreeItem {
    id: string; // path-based: "sub-1", "sub-1/area-5", "sub-1/area-5/wp-12"
    label: string;
    type: FlatItemType;
    depth: number;
    hasChildren: boolean;
    parentId: string | null;
    subcompanyId: number;
    areaId?: number;
    workPosition?: WorkPositionTreeNode;
}

type SubcompanyEntry = { subcompany: Subcompany; rootAreas: AreaTreeNode[] };

// Recursive helper: populates map with all tree items regardless of expansion state.
function populateMap(areas: AreaTreeNode[], parentId: string, depth: number, subcompanyId: number, map: Map<string, FlatTreeItem>) {
    for (const area of areas) {
        const areaId = `${parentId}/area-${area.area_id}`;
        const hasChildren = (area.subareas?.length ?? 0) > 0 || (area.work_positions?.length ?? 0) > 0;
        map.set(areaId, { id: areaId, label: area.area_name, type: 'area', depth, hasChildren, parentId, subcompanyId, areaId: area.area_id });
        populateMap(area.subareas ?? [], areaId, depth + 1, subcompanyId, map);
        for (const wp of area.work_positions ?? []) {
            const wpId = `${areaId}/wp-${wp.work_position_id}`;
            map.set(wpId, {
                id: wpId,
                label: wp.work_position_name,
                type: 'work_position',
                depth: depth + 1,
                hasChildren: false,
                parentId: areaId,
                subcompanyId,
                areaId: area.area_id,
                workPosition: wp,
            });
        }
    }
}

function buildAllItemsMap(tree: SubcompanyEntry[]): Map<string, FlatTreeItem> {
    const map = new Map<string, FlatTreeItem>();
    for (const { subcompany, rootAreas } of tree) {
        const subId = `sub-${subcompany.subcompany_id}`;
        map.set(subId, { id: subId, label: subcompany.subcompany_name, type: 'subcompany', depth: 0, hasChildren: rootAreas.length > 0, parentId: null, subcompanyId: subcompany.subcompany_id });
        populateMap(rootAreas, subId, 1, subcompany.subcompany_id, map);
    }
    return map;
}

// Recursive helper: pushes only visible items into result, respecting expandedIds.
function pushVisible(areas: AreaTreeNode[], parentId: string, depth: number, subcompanyId: number, expandedIds: Set<string>, result: FlatTreeItem[]) {
    for (const area of areas) {
        const areaId = `${parentId}/area-${area.area_id}`;
        const hasChildren = (area.subareas?.length ?? 0) > 0 || (area.work_positions?.length ?? 0) > 0;
        result.push({ id: areaId, label: area.area_name, type: 'area', depth, hasChildren, parentId, subcompanyId, areaId: area.area_id });
        if (expandedIds.has(areaId)) {
            pushVisible(area.subareas ?? [], areaId, depth + 1, subcompanyId, expandedIds, result);
            for (const wp of area.work_positions ?? []) {
                const wpId = `${areaId}/wp-${wp.work_position_id}`;
                result.push({
                    id: wpId,
                    label: wp.work_position_name,
                    type: 'work_position',
                    depth: depth + 1,
                    hasChildren: false,
                    parentId: areaId,
                    subcompanyId,
                    areaId: area.area_id,
                    workPosition: wp,
                });
            }
        }
    }
}

function computeVisibleItems(tree: SubcompanyEntry[], expandedIds: Set<string>): FlatTreeItem[] {
    const result: FlatTreeItem[] = [];
    for (const { subcompany, rootAreas } of tree) {
        const subId = `sub-${subcompany.subcompany_id}`;
        result.push({ id: subId, label: subcompany.subcompany_name, type: 'subcompany', depth: 0, hasChildren: rootAreas.length > 0, parentId: null, subcompanyId: subcompany.subcompany_id });
        if (expandedIds.has(subId)) {
            pushVisible(rootAreas, subId, 1, subcompany.subcompany_id, expandedIds, result);
        }
    }
    return result;
}

export function useVirtualTreeState(subcompanyTree: SubcompanyEntry[], initialExpandedIds?: Set<string>) {
    const [expandedIds, setExpandedIds] = useState<Set<string>>(() => initialExpandedIds ?? new Set(subcompanyTree.map(({ subcompany }) => `sub-${subcompany.subcompany_id}`)));

    // Complete map of all items (ignores expansion) — used by getItem and search.
    const allItemsMap = useMemo(() => buildAllItemsMap(subcompanyTree), [subcompanyTree]);

    // Flat ordered list of currently visible items.
    const flatVisibleItems = useMemo(() => computeVisibleItems(subcompanyTree, expandedIds), [subcompanyTree, expandedIds]);

    const toggleExpansion = useCallback((id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        const all = new Set<string>();
        for (const [id, item] of allItemsMap) {
            if (item.hasChildren) all.add(id);
        }
        setExpandedIds(all);
    }, [allItemsMap]);

    const collapseAll = useCallback(() => setExpandedIds(new Set()), []);

    const setItemExpansion = useCallback((id: string, isExpanded: boolean) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (isExpanded) next.add(id);
            else next.delete(id);
            return next;
        });
    }, []);

    // Adds all ancestors of id to expandedIds (path split: "sub-1/area-5/wp-12" → ["sub-1", "sub-1/area-5"]).
    const expandAncestors = useCallback((id: string) => {
        const parts = id.split('/');
        if (parts.length <= 1) return;
        setExpandedIds((prev) => {
            const next = new Set(prev);
            for (let i = 1; i < parts.length; i++) {
                next.add(parts.slice(0, i).join('/'));
            }
            return next;
        });
    }, []);

    return { flatVisibleItems, expandedIds, allItemsMap, toggleExpansion, expandAll, collapseAll, setItemExpansion, expandAncestors };
}
